import { useState, useEffect, useCallback, useMemo } from 'react';
import { apiClient } from '@services/api';
import { emitActivityChanged } from '@utils/activityEvents';
import StatusCard, { StatusCardMetadata } from './StatusCard';
import './LaundryMenuCard.scss';

// ── Types matching API DTOs ──

interface LaundryItem {
  id: number;
  name: string;
  nameTranslationKey?: string;
  description?: string;
  descriptionTranslationKey?: string;
  icon: string;
  price: number;
  maxQuantity: number;
}

interface LaundryCategory {
  id: number;
  name: string;
  nameTranslationKey?: string;
  icon: string;
  items: LaundryItem[];
}

interface LaundryMenuData {
  isEnabled: boolean;
  welcomeMessage?: string;
  estimatedProcessingHours: number;
  maxRequestsPerDay: number;
  requestsToday: number;
  canRequestMore: boolean;
  guestName: string;
  roomNumber: string;
  currencyCode: string;
  currencySymbol: string;
  expressEnabled: boolean;
  expressCutoffTime?: string;
  expressPricingType: string;
  expressExtraCharge: number;
  categories: LaundryCategory[];
}

interface CartItem {
  item: LaundryItem;
  quantity: number;
}

export interface LaundryMenuCardMetadata {
  type: 'laundry_menu';
  hotelSlug: string;
}

interface LaundryMenuCardProps {
  metadata: LaundryMenuCardMetadata;
  messageId?: string;
  onOrderPlaced?: (orderId: string, statusMeta?: StatusCardMetadata) => void;
}

export default function LaundryMenuCard({ metadata, messageId, onOrderPlaced }: LaundryMenuCardProps) {
  const [menuData, setMenuData] = useState<LaundryMenuData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [activeCategory, setActiveCategory] = useState<number | null>(null);
  const [placing, setPlacing] = useState(false);
  const [isExpress, setIsExpress] = useState(false);
  const [notes, setNotes] = useState('');
  const [showNotes, setShowNotes] = useState(false);
  const [showPriceConfirm, setShowPriceConfirm] = useState(false);
  const [orderResult, setOrderResult] = useState<{
    orderId: string;
    orderNumber?: string;
    message: string;
    items?: { name: string; quantity: number; price: number }[];
    totalPrice?: number;
    isExpress?: boolean;
  } | null>(null);

  const { hotelSlug } = metadata;

  const loadMenu = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await apiClient.get<LaundryMenuData>(`/api/v1/${hotelSlug}/laundry/menu`);
      setMenuData(data);
      if (data.categories?.length > 0) {
        setActiveCategory(data.categories[0].id);
      }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } } };
      setError(e.response?.data?.error || 'Failed to load laundry menu');
    } finally {
      setLoading(false);
    }
  }, [hotelSlug]);

  useEffect(() => {
    loadMenu();
  }, [loadMenu]);

  // Cart helpers
  const getCartItem = (itemId: number) => cart.find(c => c.item.id === itemId);
  const cartCount = useMemo(() => cart.reduce((sum, c) => sum + c.quantity, 0), [cart]);
  const cartSubtotal = useMemo(() => cart.reduce((sum, c) => sum + c.item.price * c.quantity, 0), [cart]);

  const expressSurcharge = useMemo(() => {
    if (!isExpress || !menuData) return 0;
    if (menuData.expressPricingType === 'percentage') {
      return cartSubtotal * (menuData.expressExtraCharge / 100);
    }
    return menuData.expressExtraCharge;
  }, [isExpress, cartSubtotal, menuData]);

  const cartTotal = cartSubtotal + expressSurcharge;

  const addToCart = (item: LaundryItem) => {
    const existing = getCartItem(item.id);
    if (existing) {
      if (existing.quantity >= item.maxQuantity) return;
      setCart(prev => prev.map(c =>
        c.item.id === item.id ? { ...c, quantity: c.quantity + 1 } : c
      ));
    } else {
      setCart(prev => [...prev, { item, quantity: 1 }]);
    }
  };

  const removeFromCart = (itemId: number) => {
    const existing = getCartItem(itemId);
    if (!existing) return;
    if (existing.quantity <= 1) {
      setCart(prev => prev.filter(c => c.item.id !== itemId));
    } else {
      setCart(prev => prev.map(c =>
        c.item.id === itemId ? { ...c, quantity: c.quantity - 1 } : c
      ));
    }
  };

  // Place order
  const placeOrder = async () => {
    if (cart.length === 0 || placing) return;
    setPlacing(true);
    try {
      const { data } = await apiClient.post<{ id: string; orderNumber?: string; totalPrice: number; isExpress: boolean; thankYouMessage?: string }>(`/api/v1/${hotelSlug}/laundry/order`, {
        items: cart.map(c => ({ itemId: c.item.id, quantity: c.quantity })),
        notes: notes || undefined,
        isExpress,
        messageId: messageId || undefined,
      });

      const orderItems = cart.map(c => ({
        name: c.item.name,
        quantity: c.quantity,
        price: c.item.price * c.quantity,
      }));

      setOrderResult({
        orderId: data.id,
        orderNumber: data.orderNumber,
        message: data.thankYouMessage || 'Laundry order placed!',
        items: orderItems,
        totalPrice: data.totalPrice,
        isExpress: data.isExpress,
      });
      setCart([]);

      const statusMeta: StatusCardMetadata = {
        type: 'laundry',
        referenceId: data.id,
        status: 'Pending',
        items: orderItems,
        totalAmount: data.totalPrice,
        orderNumber: data.orderNumber,
      };

      onOrderPlaced?.(data.id, statusMeta);
      emitActivityChanged();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } } };
      setError(e.response?.data?.error || 'Failed to place laundry order');
    } finally {
      setPlacing(false);
    }
  };

  // ── Renders ──

  if (loading) {
    return (
      <div className="laundry-card laundry-card--loading">
        <div className="laundry-card__header">
          <span className="laundry-card__header-icon">👔</span>
          <span className="laundry-card__header-title">Laundry Service</span>
        </div>
        <div className="laundry-card__loader">
          <div className="laundry-card__spinner" />
          <span>Loading laundry menu...</span>
        </div>
      </div>
    );
  }

  if (error || !menuData) {
    return (
      <div className="laundry-card laundry-card--error">
        <div className="laundry-card__header">
          <span className="laundry-card__header-icon">👔</span>
          <span className="laundry-card__header-title">Laundry Service</span>
        </div>
        <div className="laundry-card__error">{error || 'Laundry service unavailable'}</div>
      </div>
    );
  }

  if (orderResult) {
    const trackerMetadata: StatusCardMetadata = {
      type: 'laundry',
      referenceId: orderResult.orderId,
      status: 'Pending',
      items: orderResult.items,
      totalAmount: orderResult.totalPrice,
      orderNumber: orderResult.orderNumber,
    };
    return <StatusCard metadata={trackerMetadata} />;
  }

  if (!menuData.isEnabled) {
    return (
      <div className="laundry-card laundry-card--error">
        <div className="laundry-card__header">
          <span className="laundry-card__header-icon">👔</span>
          <span className="laundry-card__header-title">Laundry Service</span>
        </div>
        <div className="laundry-card__error">Laundry service is currently unavailable.</div>
      </div>
    );
  }

  if (!menuData.canRequestMore) {
    return (
      <div className="laundry-card laundry-card--limit">
        <div className="laundry-card__header">
          <span className="laundry-card__header-icon">👔</span>
          <span className="laundry-card__header-title">Laundry Service</span>
        </div>
        <div className="laundry-card__limit">
          Daily request limit reached ({menuData.requestsToday}/{menuData.maxRequestsPerDay})
        </div>
      </div>
    );
  }

  const currentCategory = menuData.categories.find(c => c.id === activeCategory) || menuData.categories[0];
  const sym = menuData.currencySymbol;

  return (
    <div className="laundry-card">
      {/* Header */}
      <div className="laundry-card__header">
        <span className="laundry-card__header-icon">👔</span>
        <span className="laundry-card__header-title">Laundry Service</span>
        {menuData.estimatedProcessingHours > 0 && (
          <span className="laundry-card__header-badge">~{menuData.estimatedProcessingHours}h</span>
        )}
      </div>

      {/* Category tabs */}
      {menuData.categories.length > 1 && (
        <div className="laundry-card__categories">
          {menuData.categories.map(cat => (
            <button
              key={cat.id}
              className={`laundry-card__cat-btn ${activeCategory === cat.id ? 'active' : ''}`}
              onClick={() => setActiveCategory(cat.id)}
            >
              {cat.icon} {cat.name}
            </button>
          ))}
        </div>
      )}

      {/* Items */}
      <div className="laundry-card__items">
        {currentCategory?.items.map(item => {
          const inCart = getCartItem(item.id);

          return (
            <div
              key={item.id}
              className={`laundry-card__item ${inCart ? 'in-cart' : ''}`}
            >
              <div className="laundry-card__item-main">
                <div className="laundry-card__item-info">
                  <span className="laundry-card__item-icon">{item.icon}</span>
                  <div className="laundry-card__item-text">
                    <span className="laundry-card__item-name">{item.name}</span>
                    {item.description && (
                      <span className="laundry-card__item-desc">{item.description}</span>
                    )}
                  </div>
                  <span className="laundry-card__item-price">{sym}{item.price.toFixed(2)}</span>
                </div>

                {/* Quantity controls */}
                <div className="laundry-card__qty">
                  {inCart ? (
                    <>
                      <button className="laundry-card__qty-btn" onClick={() => removeFromCart(item.id)}>−</button>
                      <span className="laundry-card__qty-val">{inCart.quantity}</span>
                      <button
                        className="laundry-card__qty-btn"
                        onClick={() => addToCart(item)}
                        disabled={inCart.quantity >= item.maxQuantity}
                      >+</button>
                    </>
                  ) : (
                    <button className="laundry-card__add-btn" onClick={() => addToCart(item)}>+</button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Express toggle */}
      {menuData.expressEnabled && cart.length > 0 && (
        <div className="laundry-card__express">
          <label className="laundry-card__express-toggle">
            <input
              type="checkbox"
              checked={isExpress}
              onChange={(e) => setIsExpress(e.target.checked)}
            />
            <span className="laundry-card__express-label">
              ⚡ Express Delivery
              <span className="laundry-card__express-price">
                +{menuData.expressPricingType === 'percentage'
                  ? `${menuData.expressExtraCharge}%`
                  : `${sym}${menuData.expressExtraCharge.toFixed(2)}`}
              </span>
            </span>
          </label>
          {menuData.expressCutoffTime && (
            <span className="laundry-card__express-cutoff">Before {menuData.expressCutoffTime}</span>
          )}
        </div>
      )}

      {/* Notes toggle */}
      {cart.length > 0 && (
        <div className="laundry-card__notes-section">
          <button
            className="laundry-card__notes-toggle"
            onClick={() => setShowNotes(!showNotes)}
          >
            📝 {showNotes ? 'Hide notes' : 'Add notes'}
          </button>
          {showNotes && (
            <input
              type="text"
              className="laundry-card__notes-input"
              placeholder="Special instructions (e.g. delicate fabric)..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={200}
            />
          )}
        </div>
      )}

      {/* Cart summary + place order */}
      {cart.length > 0 && (
        <div className="laundry-card__cart">
          <div className="laundry-card__cart-summary">
            <span className="laundry-card__cart-count">
              {cartCount} item{cartCount > 1 ? 's' : ''}
            </span>
            <div className="laundry-card__cart-pricing">
              {isExpress && expressSurcharge > 0 && (
                <span className="laundry-card__cart-express">⚡ +{sym}{expressSurcharge.toFixed(2)}</span>
              )}
              <span className="laundry-card__cart-total">{sym}{cartTotal.toFixed(2)}</span>
            </div>
          </div>
          <button
            className="laundry-card__order-btn"
            onClick={() => {
              if (cartTotal > 0) {
                setShowPriceConfirm(true);
              } else {
                placeOrder();
              }
            }}
            disabled={placing || showPriceConfirm}
          >
            {placing ? (
              <span className="laundry-card__spinner-sm" />
            ) : (
              'Place Laundry Order'
            )}
          </button>
        </div>
      )}

      {/* Price confirmation dialog */}
      {showPriceConfirm && (
        <div style={{ background: '#EFF6FF', border: '1px solid #3B82F6', borderRadius: 8, padding: '12px 14px', margin: '0 8px 8px' }}>
          <div style={{ fontSize: 13, color: '#1E40AF', marginBottom: 6, fontWeight: 600 }}>
            💳 Order Total: {sym}{cartTotal.toFixed(2)}
          </div>
          {isExpress && expressSurcharge > 0 && (
            <div style={{ fontSize: 12, color: '#1E40AF', marginBottom: 4 }}>
              Includes express surcharge: {sym}{expressSurcharge.toFixed(2)}
            </div>
          )}
          <div style={{ fontSize: 12, color: '#1E40AF', marginBottom: 10 }}>
            This order will be charged to your folio. Please confirm to proceed.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              style={{ flex: 1, padding: '6px 12px', borderRadius: 6, border: '1px solid #2563EB', background: '#3B82F6', color: '#fff', fontSize: 13, cursor: 'pointer' }}
              onClick={() => { setShowPriceConfirm(false); placeOrder(); }}
              disabled={placing}
            >
              {placing ? 'Placing...' : 'Confirm & Place Order'}
            </button>
            <button
              style={{ flex: 1, padding: '6px 12px', borderRadius: 6, border: '1px solid #ccc', background: '#fff', color: '#666', fontSize: 13, cursor: 'pointer' }}
              onClick={() => setShowPriceConfirm(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
